package com.upcominds.expedite.features.energy

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.upcominds.expedite.data.energy.CommunityEnergyRepository
import com.upcominds.expedite.data.mobilelogs.logMobileError
import com.upcominds.expedite.dto.CommunityEnergyDTO
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlin.math.round

data class CommunityEnergyUiState(
    val communityId: String? = null,
    val loading: Boolean = false,
    val busy: Boolean = false,
    val error: String? = null,
    val data: CommunityEnergyDTO? = null
)

class CommunityEnergyViewModel(private val repository: CommunityEnergyRepository) : ViewModel() {
    private val state = MutableStateFlow(CommunityEnergyUiState())
    val uiState = state.asStateFlow()
    private var job: Job? = null

    fun loadMember(id: String?) = load(id, false)
    fun loadAdmin(id: String) = load(id, true)

    private fun load(id: String?, admin: Boolean) {
        job?.cancel()
        if (id == null) {
            state.value = CommunityEnergyUiState()
            return
        }
        val previous = state.value.data?.takeIf { it.communityId == id }
        state.value = CommunityEnergyUiState(communityId = id, loading = true, data = previous)
        job = viewModelScope.launch {
            try {
                val data = if (admin) repository.getAdminEnergy(id) else repository.getMemberEnergy(id)
                state.value = CommunityEnergyUiState(communityId = id, data = data)
            } catch (error: Exception) {
                if (error is CancellationException) throw error
                logMobileError("CommunityEnergyViewModel.load", error)
                state.value = state.value.copy(loading = false, error = "Building data could not be loaded. Please try again.")
            }
        }
    }

    fun connect(id: String, name: String?) = mutate(id) { repository.connect(id, name) }
    fun sync(id: String) = mutate(id) { repository.sync(id) }

    private fun mutate(id: String, action: suspend () -> CommunityEnergyDTO) {
        if (state.value.busy || state.value.communityId != id) return
        job?.cancel()
        state.value = state.value.copy(busy = true, error = null)
        job = viewModelScope.launch {
            try {
                val data = action()
                state.value = CommunityEnergyUiState(communityId = id, data = data)
            } catch (error: Exception) {
                if (error is CancellationException) throw error
                state.value = state.value.copy(busy = false, error = error.message ?: "Could not update the building connection.")
            }
        }
    }
}

fun formatEnergyKwh(value: Double): String = (round(value * 100) / 100).toString()
fun energyTimestamp(value: String?): String = value?.replace('T', ' ')?.take(16)?.let { "$it UTC" } ?: "Not synchronized yet"
